import i18n, { changeLanguage } from "i18n";
import React from "react";

import { ISessionUser } from "@/data/session";

import { IResolvedSession, ISession } from "./types";

export const SessionContext = React.createContext({} as ISession<any>);

const getDateSignature = () => new Date().getTime();

interface Props {}

interface State<T> extends ISession<T> {}

export class SessionProvider<T> extends React.Component<Props, State<T>> {
  constructor(props: Props) {
    super(props);

    this.state = {
      authenticated: false,
      pristine: true,

      create: this.create,
      update: this.update,
      destroy: this.destroy,
      markAsPristine: this.markAsPristine,
    };
  }

  changeLanguage(payload: any) {
    const { language } = payload as ISessionUser;

    if (language && language !== i18n.language) {
      changeLanguage(language);
    }
  }

  create = (payload: T, cb?: () => void) => {
    this.setState({ authenticated: true, pristine: false, payload, creationDate: getDateSignature() }, () => {
      this.changeLanguage(payload);
      if (cb) {
        cb();
      }
    });
  };

  update = (payload: T, cb?: () => void) => {
    this.setState({ payload, modificationDate: getDateSignature() }, () => {
      this.changeLanguage(payload);
      if (cb) {
        cb();
      }
    });
  };

  destroy = (cb?: () => void) => {
    this.setState(
      { authenticated: false, payload: undefined, creationDate: undefined, modificationDate: undefined },
      () => {
        if (cb) {
          cb();
        }
      },
    );
  };

  markAsPristine = (pristine: boolean) => {
    this.setState({ pristine });
  };

  render() {
    const { children } = this.props;

    return <SessionContext.Provider value={this.state}>{children}</SessionContext.Provider>;
  }
}

export const SessionConsumer = SessionContext.Consumer;

export interface IWithSessionProps<T> {
  session: ISession<T>;
}

export function withSession<P extends IWithSessionProps<T>, T = any>(WrappedComponent: React.ComponentType<P>) {
  return class WithUser extends React.Component<Omit<P, keyof IWithSessionProps<T>>> {
    render() {
      return (
        <SessionConsumer>
          {session => <WrappedComponent key={session.creationDate} session={session} {...(this.props as any)} />}
        </SessionConsumer>
      );
    }
  };
}

export interface IWithResolvedSessionProps<T> {
  session: IResolvedSession<T>;
}

export function withResolvedSession<P extends IWithResolvedSessionProps<T>, T = any>(
  WrappedComponent: React.ComponentType<P>,
) {
  return class WithResolvedSession extends React.Component<Omit<P, keyof IWithResolvedSessionProps<T>>> {
    render() {
      return (
        <SessionConsumer>
          {session => {
            const resolvedSession = session as IResolvedSession<T>;
            const { authenticated, pristine } = resolvedSession;

            if (!pristine && !authenticated) {
              return this.props.children;
            }

            return <WrappedComponent key={session.creationDate} session={resolvedSession} {...(this.props as any)} />;
          }}
        </SessionConsumer>
      );
    }
  };
}
